import { Button } from '@vrplatform/components';
import {
  useBilling,
  useIntercom,
  useSubscription,
  useTeam,
  useTeamRole,
  useTrpcQuery,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { day } from '@vrplatform/utils';
import { PaymentMethod } from '@hyperline/react-components';
import {
  Anchor,
  Box,
  type BoxProps,
  Center,
  type MantineSize,
  type MantineSpacing,
  Modal,
  Skeleton,
  Stack,
  type StyleProp,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useState,
} from 'react';
import { HiddenFeatureIndicator } from './HiddenFeatureIndicator';

export const UpgradeSubscriptionButton = ({
  size = 'xs',
  variant,
  children = 'Upgrade now',
  onSuccess,
}: {
  size?: MantineSize;
  variant?: ComponentProps<typeof Button>['variant'];
  children?: ReactNode;
  onSuccess?: () => void;
}) => {
  const [opened, handlers] = useDisclosure(false);
  const { isTeamAdmin } = useTeamRole();

  if (!isTeamAdmin) return null;

  return (
    <>
      <Button
        size={size}
        variant={variant}
        onClick={handlers.open}
        data-testid="upgrade-trial-button"
      >
        {children}
      </Button>

      <PaymentModal
        closeModal={handlers.close}
        opened={opened}
        onSuccess={onSuccess}
      />
    </>
  );
};

const PaymentModal = ({
  closeModal,
  opened,
  onSuccess,
}: {
  opened: boolean;
  closeModal: () => void;
  onSuccess?: () => void;
}) => {
  const {
    subscription: { trialUntil },
  } = useBilling();

  return (
    <Modal
      centered
      opened={opened}
      onClose={closeModal}
      withinPortal
      size={600}
      title="Upgrade your subscription"
      styles={(theme) => ({
        title: {
          fontWeight: 500,
          fontSize: theme.fontSizes.lg,
        },
      })}
    >
      <UpgradeSubscriptionPaymentForm
        title=""
        description={`Your free trial will continue until ${day(trialUntil).format('LL')} and you won't be charged until then.`}
        onSuccess={() => {
          closeModal();
          onSuccess?.();
        }}
        containerProps={{}}
        spacing={rem(40)}
      />
    </Modal>
  );
};

type Props = {
  title: string;
  description?: string;
  onSuccess?: () => void;
  containerProps?: BoxProps;
  spacing?: StyleProp<MantineSpacing>;
  showDashboard?: () => void;
};

export const UpgradeSubscriptionPaymentForm = ({
  title,
  description = 'Ready to take the next step? Unlock all premium features and keep your progress with our subscription plan.',
  onSuccess,
  containerProps = {
    maw: 500,
    pt: rem(80),
  },
  spacing = rem(80),
  showDashboard,
}: Props) => {
  const { show } = useIntercom();
  const [loadingRedirect, setLoadingRedirect] = useState(false);

  return (
    <Box w="100%" mx="auto" {...containerProps}>
      {title && (
        <Title order={2} mb="sm" data-testid="payment-form-title">
          {title}
        </Title>
      )}
      <Text c="neutral" mb={spacing}>
        {description}
      </Text>
      {loadingRedirect ? (
        <AwaitingRefresh onSuccess={onSuccess} />
      ) : (
        <PaymentForm setRedirectLoading={setLoadingRedirect} />
      )}
      <Text c="gray" mt={spacing}>
        Do you need a help? Don't hesitate to{' '}
        <Anchor component="button" onClick={show}>
          contact our team
        </Anchor>
        .
      </Text>
      {showDashboard && (
        <HiddenFeatureIndicator permission="vrp-admin" mt="md" p="xs">
          <Button
            onClick={showDashboard}
            sx={{
              width: '100%',
            }}
            variant="primary"
          >
            Show data for current session
          </Button>
        </HiddenFeatureIndicator>
      )}
    </Box>
  );
};

const AwaitingRefresh = ({ onSuccess }: { onSuccess: Props['onSuccess'] }) => {
  const [{ id: teamId }, refetchTeam] = useTeam();
  const {
    subscription: { status: currentBillingStatus },
  } = useBilling();

  const { data: status } = useSubscription(
    (q, args) => {
      const tenant = q.tenantById({
        id: args.teamId,
      });

      return tenant?.billingSubscriptionStatus as
        | typeof currentBillingStatus
        | undefined;
    },
    {
      teamId,
    },
    {
      skip: !teamId,
    }
  );

  useEffect(() => {
    if (status === 'active') {
      refetchTeam();
      onSuccess?.();
    }
  }, [status, refetchTeam, onSuccess]);

  return (
    <Center
      mih={'40dvh'}
      sx={{
        flexDirection: 'column',
      }}
    >
      <LoadingIndicator size="md" type="dots" />
      <Text c="neutral" fw={500} size="md" mt="lg">
        Awaiting Confirmation
      </Text>
      <Text c="gray" size="sm" mt="xs" ta="center">
        We are confirming your payment method. This may take a few seconds.
      </Text>
    </Center>
  );
};

const PaymentForm = ({
  setRedirectLoading,
}: {
  setRedirectLoading: (v: boolean) => void;
}) => {
  const [{ id: teamId }] = useTeam();

  const { loading, data, refetch, error } = useTrpcQuery(
    'getHyperlineComponentToken',
    {
      tenantId: teamId,
    },
    {
      skip: !teamId,
      hideErrorNotification: true,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  if (loading)
    return (
      <Stack>
        <Skeleton height={50} />
        <Skeleton height={50} />
        <Skeleton height={50} />
      </Stack>
    );

  if (error || !data?.token?.token)
    return (
      <Stack pt="xl" align="center">
        <Icon icon="AlertTriangleIcon" size={32} color="red" />

        <Text
          c="neutral"
          ta="center"
          component="p"
          size="md"
          fw={500}
          mt={0}
          mb="xs"
        >
          We failed to load the payment form
        </Text>
        <Text c="gray" ta="center" component="p" size="sm" mt={0} mb={rem(40)}>
          {error?.message ||
            'Please reach out to support if the issue persists.'}
        </Text>

        <Button
          variant="primary"
          sx={{
            width: '100%',
            maxWidth: 300,
          }}
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </Stack>
    );

  return (
    <Box mih="300px">
      <PaymentMethod
        className="payment-method-iframe"
        options={{
          token: data.token.token,
          mode: data.isProduction ? 'production' : 'sandbox',
          locale: 'en',
          appearance: {
            variables: {},
          },
        }}
        onPaymentMethodCreated={() => {
          setRedirectLoading(true);
        }}
        height={'100%'}
        style={{
          overflow: 'hidden',
        }}
      />
    </Box>
  );
};
